import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { TransactionFilters } from '../components/TransactionFilters';
import { TransactionDetailsPanel } from '../components/TransactionDetailsPanel';
import { DocumentUploadButton } from '../components/DocumentUploadButton';
import { DocumentDialog } from '../components/DocumentDialog';
import { Pagination } from '../components/Pagination';
import { LoadingScreen } from '../components/LoadingScreen';
import { TransactionAPI, SalesAPI, TransferableAgent, TransactionFilterOptions } from '../services/api';
import { UnifiedTransaction } from '../types/payment';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/rolePermissions';
import { AlertService } from '../services/AlertService';
import { useTransactionFilters, toBackendFilters } from '../hooks/useTransactionFilters';
import { VoidRefundPanel } from '../components/VoidRefundPanel';
import './TransactionsTable.css';

export const PaymentHistory: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const highlight = searchParams.get('highlight');
  const [transactions, setTransactions] = useState<UnifiedTransaction[]>([]);
  // True totals over the FULL dataset (server aggregate) — the loaded array is
  // only one page, so its length/sum would undercount.
  const [txStats, setTxStats] = useState<{ total: number; total_amount: number }>({ total: 0, total_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<UnifiedTransaction | null>(null);
  const [decryptedData, setDecryptedData] = useState<any>(null);
  const [showEncryptedData, setShowEncryptedData] = useState<string | null>(null);
  // Void/Refund states
  const [showVoidRefundPanel, setShowVoidRefundPanel] = useState(false);
  const [voidRefundTransaction, setVoidRefundTransaction] = useState<UnifiedTransaction | null>(null);
  const [voidRefundAction, setVoidRefundAction] = useState<'void' | 'refund'>('void');
  const [transactionEligibility, setTransactionEligibility] = useState<{[key: string]: any}>({});
  // Use a single state object for sorting to avoid state batching issues
  const [sortConfig, setSortConfig] = useState<{
    field: keyof UnifiedTransaction;
    direction: 'asc' | 'desc';
  }>({
    field: 'created_at',
    direction: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [documentDialogRecordId, setDocumentDialogRecordId] = useState<string | null>(null);

  const canDecrypt = hasPermission(user?.role, 'canDecryptCardData');
  const canVoidRefund = !user?.view_only && user?.role && ['sales_team_lead', 'processor_team_lead', 'floor_manager', 'director', 'admin'].includes(user.role);
  // Sale-credit transfer is restricted to floor managers and above.
  const canTransfer = !user?.view_only && !!user?.role && ['floor_manager', 'director', 'admin'].includes(user.role);

  // Transfer-to-agent modal state
  const [transferTx, setTransferTx] = useState<UnifiedTransaction | null>(null);
  const [transferAgents, setTransferAgents] = useState<TransferableAgent[]>([]);
  const [transferAgentsLoading, setTransferAgentsLoading] = useState(false);
  const [transferAgentId, setTransferAgentId] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferSubmitting, setTransferSubmitting] = useState(false);

  // Prefetch the transferable-agents list once for managers. The endpoint can be
  // slow; loading it on page mount (instead of lazily on modal open) means the
  // dropdown is already populated by the time a transfer is started.
  useEffect(() => {
    if (!canTransfer) return;
    let cancelled = false;
    setTransferAgentsLoading(true);
    SalesAPI.getTransferableAgents()
      .then(agents => { if (!cancelled) setTransferAgents(agents); })
      .catch(e => { if (!cancelled) AlertService.error(e.message || 'Failed to load agents'); })
      .finally(() => { if (!cancelled) setTransferAgentsLoading(false); });
    return () => { cancelled = true; };
  }, [canTransfer]);

  // A row is transferable when it's a successful (response 100) sale-type
  // transaction — i.e. it produced a credited sale.
  const isTransferable = (t: UnifiedTransaction) =>
    canTransfer &&
    ((t.nmi_response_code || (t as any).response_code) === '100') &&
    ['sale', 'capture'].includes((t.tx_type || 'sale'));

  const openTransfer = (t: UnifiedTransaction) => {
    setTransferTx(t);
    setTransferAgentId('');
    setTransferReason('');
    // Agents are prefetched on mount; refresh in the background only if the
    // earlier load failed (empty + not currently loading) so a transient error
    // doesn't permanently strand the dropdown.
    if (transferAgents.length === 0 && !transferAgentsLoading) {
      setTransferAgentsLoading(true);
      SalesAPI.getTransferableAgents()
        .then(agents => setTransferAgents(agents))
        .catch(e => AlertService.error(e.message || 'Failed to load agents'))
        .finally(() => setTransferAgentsLoading(false));
    }
  };

  const submitTransfer = async () => {
    if (!transferTx || !transferAgentId) return;
    try {
      setTransferSubmitting(true);
      await SalesAPI.transferCredit({ transaction_id: transferTx.id, to_user_id: transferAgentId, reason: transferReason || undefined });
      AlertService.success('Sale credited to the selected agent');
      setTransferTx(null);
      loadTransactions();
    } catch (e: any) {
      AlertService.error(e.message || 'Failed to transfer sale');
    } finally {
      setTransferSubmitting(false);
    }
  };

  // Initialize filters
  const {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    filterStats
  } = useTransactionFilters(transactions);

  // Filter dropdown choices across all pages (the table only holds the current page).
  const [filterOptions, setFilterOptions] = useState<TransactionFilterOptions | null>(null);
  useEffect(() => {
    TransactionAPI.getFilterOptions().then(setFilterOptions).catch(() => setFilterOptions(null));
  }, []);

  // Refetch the server page whenever paging, sorting, or filters change.
  // (TransactionFilters debounces text input before it updates `filters`.)
  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, sortConfig, filters]);

  // When the result set or ordering changes, jump back to the first page.
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortConfig]);

  // Auto-open details panel when highlight parameter is present
  useEffect(() => {
    if (highlight && transactions.length > 0 && !loading) {
      const highlightedTransaction = transactions.find(t => t.id === highlight);
      if (highlightedTransaction) {
        setSelectedTransaction(highlightedTransaction);
        setShowDetailsPanel(true);
        // Clear the highlight from URL after opening
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [highlight, transactions, loading]);

  // Load eligibility data for void/refund when user has permission
  useEffect(() => {
    if (canVoidRefund && transactions.length > 0) {
      loadTransactionEligibility();
    }
  }, [canVoidRefund, transactions]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      // The backend only sorts a known set of columns; anything else falls back to created_at.
      const sortFieldMap: Record<string, string> = {
        amount: 'amount', created_by: 'created_by', tx_type: 'tx_type', nmi_response_code: 'response_code',
      };
      const res = await TransactionAPI.getTransactionsPaged({
        offset: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        sortField: sortFieldMap[sortConfig.field as string] || 'created_at',
        sortDir: sortConfig.direction,
        filters: toBackendFilters(filters),
      });
      setTransactions(res.items as UnifiedTransaction[]);
      setTxStats({ total: res.total, total_amount: res.total_amount });
    } catch (error: any) {
      console.error('Failed to load transactions:', error);
      AlertService.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (transaction: UnifiedTransaction) => {
    setSelectedTransaction(transaction);
    setShowDetailsPanel(true);
    // Clear any previous decrypted data
    setDecryptedData(null);
    setShowEncryptedData(null);
  };

  const handleCloseDetails = () => {
    setShowDetailsPanel(false);
    setSelectedTransaction(null);
    setDecryptedData(null);
    setShowEncryptedData(null);
  };

  const loadTransactionEligibility = async () => {
    if (!canVoidRefund) return;
    
    console.log('Loading eligibility for transactions:', transactions.map(t => ({ 
      id: t.id, 
      tx_type: t.tx_type, 
      response_code: t.nmi_response_code, 
      has_nmi_id: !!t.nmi_transactionid || !!t.nmi_transaction_id 
    })));
    
    try {
      const eligibilityPromises = transactions.map(async (transaction) => {
        // Skip void and refund transactions as they can't be voided/refunded again
        if (transaction.tx_type === 'void' || transaction.tx_type === 'refund') {
          return { [transaction.id]: { can_void: false, can_refund: false, ineligible_reason: 'Already processed' } };
        }
        
        try {
          // Let the backend determine eligibility for all other transactions
          const eligibility = await TransactionAPI.getTransactionEligibility(transaction.id);
          console.log(`Eligibility for ${transaction.id}:`, eligibility);
          return { [transaction.id]: eligibility };
        } catch (error) {
          console.error(`Failed to check eligibility for transaction ${transaction.id}:`, error);
          return { [transaction.id]: { can_void: false, can_refund: false, ineligible_reason: 'Failed to check eligibility' } };
        }
      });
      
      const results = await Promise.all(eligibilityPromises);
      const eligibilityMap = results.reduce((acc, result) => ({ ...acc, ...result }), {});
      setTransactionEligibility(eligibilityMap);
    } catch (error) {
      console.error('Failed to load transaction eligibility:', error);
    }
  };

  const handleVoidRefund = (transaction: UnifiedTransaction, action: 'void' | 'refund') => {
    setVoidRefundTransaction(transaction);
    setVoidRefundAction(action);
    setShowVoidRefundPanel(true);
  };

  const handleCloseVoidRefund = () => {
    setShowVoidRefundPanel(false);
    setVoidRefundTransaction(null);
  };

  const handleVoidRefundSuccess = () => {
    // Reload transactions to reflect changes
    loadTransactions();
    // Reload eligibility data
    if (canVoidRefund) {
      loadTransactionEligibility();
    }
  };

  const handleDecryptInPanel = async (transactionId: string, reason: string) => {
    try {
      const data = await TransactionAPI.decryptTransactionData(transactionId, reason);
      setDecryptedData(data);
      setShowEncryptedData(transactionId);
      AlertService.success('Transaction data decrypted successfully');
    } catch (error: any) {
      console.error('Failed to decrypt transaction data:', error);
      AlertService.error(error.message || 'Failed to decrypt transaction data');
    }
  };

  const handleSort = (field: keyof UnifiedTransaction) => {
    setSortConfig(prevConfig => {
      let newDirection: 'asc' | 'desc';
      
      if (prevConfig.field === field) {
        // Same field clicked - toggle direction
        newDirection = prevConfig.direction === 'asc' ? 'desc' : 'asc';
      } else {
        // Different field clicked - default to descending for most fields, ascending for names
        newDirection = field === 'created_by' || field === 'first_name' || field === 'last_name' ? 'asc' : 'desc';
      }
      
      return {
        field: field,
        direction: newDirection
      };
    });
  };

  // The server already returns the correctly sorted + paginated page, so the
  // table renders these rows as-is (no client-side sort/slice).
  const sortedAndPaginatedTransactions = transactions;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const maskCreditCard = (ccNumber: string | undefined, masked: string | undefined, userRole: string | undefined): string => {
    if (masked) return masked;
    if (!ccNumber) return '****-****-****-****';
    
    // Show full card number only for admin and director roles
    if (userRole === 'admin' || userRole === 'director') {
      return ccNumber;
    }
    
    // For other roles, show masked version
    if (ccNumber.length >= 4) {
      const last4 = ccNumber.slice(-4);
      return `****-****-****-${last4}`;
    }
    
    return '****-****-****-****';
  };

  const formatCardData = (transaction: UnifiedTransaction): string => {
    const userRole = user?.role;
    
    if (canDecrypt && decryptedData && showEncryptedData === transaction.id) {
      // Show decrypted data for admin/director
      const fullCard = decryptedData.decrypted_pan || '';
      return maskCreditCard(fullCard, transaction.masked_pan, userRole);
    }
    
    // Show masked version based on role
    if (transaction.last4) {
      return maskCreditCard(`************${transaction.last4}`, transaction.masked_pan, userRole);
    }
    
    return transaction.masked_pan || '****-****-****-****';
  };

  return (
    <DashboardLayout>
      <div className="payment-history">
        <div className="page-header">
          <div>
            <h1>Paid Transactions</h1>
            <p>View and manage successful payment transactions</p>
          </div>
          <button className="btn btn-outline-primary" onClick={loadTransactions} disabled={loading}>
            {loading ? 'Loading...' : '🔄 Refresh'}
          </button>
        </div>

        {/* Summary — true totals over the full dataset (server aggregate).
            Gate on txStats (which persists across a refetch) rather than `!loading`,
            so the cards don't blink out every time a filter triggers a reload. */}
        {(txStats.total > 0 || hasActiveFilters) && (
          <div className="ph-summary">
            <div className="ph-summary-card">
              <div className="ph-summary-number">{txStats.total.toLocaleString()}</div>
              <div className="ph-summary-label">Total Transactions</div>
            </div>
            <div className="ph-summary-card">
              <div className="ph-summary-number">
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(txStats.total_amount)}
              </div>
              <div className="ph-summary-label">Total Amount</div>
            </div>
          </div>
        )}

        {/* Filters — keep visible while a filter is active even if it returns nothing,
            so the user can always adjust or clear it. Do NOT gate on `!loading`: with
            server-side pagination every keystroke triggers a fetch (loading=true), and
            unmounting the panel mid-type would collapse it and steal input focus, so you
            could only ever enter the first character / one filter at a time. */}
        {(transactions.length > 0 || hasActiveFilters) && (
          <TransactionFilters
            filters={filters}
            updateFilter={updateFilter}
            clearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
            transactions={transactions}
            filterStats={filterStats}
            showVoidRefundFilters={true}
            showProcessingStatusFilter={false}
            showSaleFilters={true}
            options={filterOptions}
          />
        )}

        {/* Full-screen overlay only on the very first load (nothing on screen yet).
            Filter-driven refetches reload in the background so the table and filter
            panel stay visible and interactive — otherwise the opaque overlay would
            blank the screen on every committed filter change. */}
        <LoadingScreen
          isVisible={loading && transactions.length === 0 && !hasActiveFilters}
          message="Loading payment history..."
        />

        {transactions.length > 0 ? (
          <div className="transactions-table-container">
            <div className="table-wrapper">
              <table className="transactions-table">
                <thead>
                  <tr>
                    {user?.role !== 'agent' && (
                      <th onClick={() => handleSort('created_by')} className="sortable">
                        Created By
                        {sortConfig.field === 'created_by' && (
                          <span className="sort-indicator">
                            {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                          </span>
                        )}
                      </th>
                    )}
                    <th>TFN</th>
                    <th onClick={() => handleSort('amount')} className="sortable">
                      Amount
                      {sortConfig.field === 'amount' && (
                        <span className="sort-indicator">
                          {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </th>
                    <th>State</th>
                    <th>Category</th>
                    <th>
                      Credit Card
                    </th>
                    <th>Processor</th>
                    <th onClick={() => handleSort('created_at')} className="sortable">
                      Date & Time
                      {sortConfig.field === 'created_at' && (
                        <span className="sort-indicator">
                          {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </th>
                    {user?.role === 'agent' ? (
                      <th className="void-refund-status-column">
                        Void/Refund Status
                      </th>
                    ) : canVoidRefund ? (
                      <th className="void-refund-column">
                        Void/Refund
                      </th>
                    ) : null}
                    <th className="actions-column">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAndPaginatedTransactions.map((transaction, index) => (
                    <tr key={`${transaction.id}-${index}`}>
                      {user?.role !== 'agent' && (
                        <td className="created-by-cell">
                          {transaction.created_by_name || transaction.created_by || 'Unknown'}
                        </td>
                      )}
                      <td>{transaction.tfn ? `…${String(transaction.tfn).replace(/\D/g, '').slice(-4)}` : 'N/A'}</td>
                      <td className="amount-cell">
                        <span className="amount-value">
                          {transaction.amount !== undefined && transaction.amount !== null
                            ? `$${Number(transaction.amount).toFixed(2)}`
                            : 'N/A'
                          }
                        </span>
                      </td>
                      <td>{transaction.sale_state || 'N/A'}</td>
                      <td>{transaction.category || 'N/A'}</td>
                      <td className="cc-cell">
                        <div className="cc-info">
                          <div className="cc-number">
                            {formatCardData(transaction)}
                            {transaction.three_ds_status === 'Y' && (
                              <span className="badge-3ds">3DS</span>
                            )}
                            {transaction.three_ds_status === 'Y-3RI' && (
                              <span className="badge-3ds">3RI</span>
                            )}
                          </div>
                          {transaction.card_brand && (
                            <div className="cc-brand">
                              {transaction.card_brand.toUpperCase()}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{(transaction as any).processor_name || 'N/A'}</td>
                      <td className="date-cell">
                        {new Date(transaction.created_at).toLocaleDateString()}<br/>
                        <small>{new Date(transaction.created_at).toLocaleTimeString()}</small>
                      </td>
                      {user?.role === 'agent' ? (
                        <td className="void-refund-status-cell">
                          {(() => {
                            // For agents, only show status information (no action buttons)
                            if (transaction.is_voided) {
                              return (
                                <div className="transaction-status voided">
                                  <span className="status-icon">🚫</span>
                                  <div className="status-details">
                                    <div className="status-label">Voided</div>
                                    {transaction.voided_at && (
                                      <div className="status-date">
                                        {new Date(transaction.voided_at).toLocaleString()}
                                      </div>
                                    )}
                                    {transaction.voided_by_name && (
                                      <div className="status-by">
                                        By: {transaction.voided_by_name}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            
                            if (transaction.is_refunded) {
                              return (
                                <div className="transaction-status refunded">
                                  <span className="status-icon">💰</span>
                                  <div className="status-details">
                                    <div className="status-label">Refunded</div>
                                    {transaction.refunded_at && (
                                      <div className="status-date">
                                        {new Date(transaction.refunded_at).toLocaleString()}
                                      </div>
                                    )}
                                    {transaction.refunded_by_name && (
                                      <div className="status-by">
                                        By: {transaction.refunded_by_name}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            
                            // If not voided/refunded, show "Active"
                            return (
                              <div className="transaction-status active">
                                <span className="status-icon">✅</span>
                                <div className="status-details">
                                  <div className="status-label">Active</div>
                                </div>
                              </div>
                            );
                          })()}
                        </td>
                      ) : canVoidRefund ? (
                        <td className="void-refund-cell">
                          {(() => {
                            // For other roles, show status or action buttons
                            if (transaction.is_voided) {
                              return (
                                <div className="transaction-status voided">
                                  <span className="status-icon">🚫</span>
                                  <div className="status-details">
                                    <div className="status-label">Voided</div>
                                    {transaction.voided_at && (
                                      <div className="status-date">
                                        {new Date(transaction.voided_at).toLocaleString()}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            
                            if (transaction.is_refunded) {
                              return (
                                <div className="transaction-status refunded">
                                  <span className="status-icon">💰</span>
                                  <div className="status-details">
                                    <div className="status-label">Refunded</div>
                                    {transaction.refunded_at && (
                                      <div className="status-date">
                                        {new Date(transaction.refunded_at).toLocaleString()}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            
                            // If not voided/refunded, show action buttons based on eligibility
                            const eligibility = transactionEligibility[transaction.id];
                            console.log(`Frontend: Transaction ${transaction.id} eligibility:`, eligibility);
                            if (!eligibility) {
                              return <span className="text-muted">-</span>;
                            }
                            
                            if (eligibility.can_void) {
                              return (
                                <button
                                  className="btn btn-warning btn-sm"
                                  onClick={() => handleVoidRefund(transaction, 'void')}
                                  title="Void transaction (not settled yet)"
                                >
                                  ❌ Void
                                </button>
                              );
                            } else if (eligibility.can_refund) {
                              return (
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleVoidRefund(transaction, 'refund')}
                                  title="Refund transaction"
                                >
                                  💰 Refund
                                </button>
                              );
                            } else {
                              return (
                                <span 
                                  className="text-muted"
                                  title={eligibility.ineligible_reason || 'Not eligible'}
                                >
                                  N/A
                                </span>
                              );
                            }
                          })()}
                        </td>
                      ) : null}
                      <td className="actions-cell">
                        <div className="action-buttons">
                          <DocumentUploadButton
                            recordId={transaction.id}
                            onClick={() => setDocumentDialogRecordId(transaction.id)}
                          />
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => handleViewDetails(transaction)}
                            title="View Details"
                          >
                            📋 Details
                          </button>
                          {isTransferable(transaction) && (
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => openTransfer(transaction)}
                              title="Transfer this sale's credit to an agent"
                            >
                              🔁 Transfer
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalItems={txStats.total}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              pageSizeOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        ) : !loading && !hasActiveFilters ? (
          <div className="empty-state">
            <h3>No Transactions</h3>
            <p>No transactions found.</p>
          </div>
        ) : !loading ? (
          <div className="empty-state">
            <h3>No Matching Results</h3>
            <p>No transactions match your current filter criteria.</p>
            <p>Try adjusting your filters or clearing them to see all transactions.</p>
          </div>
        ) : null}

        {/* Transaction Details Panel */}
        <TransactionDetailsPanel
          transaction={selectedTransaction}
          isOpen={showDetailsPanel}
          onClose={handleCloseDetails}
          decryptedData={showEncryptedData === selectedTransaction?.id ? decryptedData : null}
          onDecryptData={handleDecryptInPanel}
          onVoidRefund={canVoidRefund ? handleVoidRefund : undefined}
          onEdited={loadTransactions}
        />

        {/* Void/Refund Panel */}
        {voidRefundTransaction && (
          <VoidRefundPanel
            transaction={voidRefundTransaction}
            isOpen={showVoidRefundPanel}
            onClose={handleCloseVoidRefund}
            actionType={voidRefundAction}
            onSuccess={handleVoidRefundSuccess}
          />
        )}

        {/* Document Dialog */}
        {documentDialogRecordId && (
          <DocumentDialog
            recordId={documentDialogRecordId}
            isOpen={true}
            onClose={() => setDocumentDialogRecordId(null)}
            recordTitle={transactions.find(t => t.id === documentDialogRecordId)?.first_name + ' ' + transactions.find(t => t.id === documentDialogRecordId)?.last_name}
          />
        )}

        {/* Transfer-to-agent modal */}
        {transferTx && (
          <div className="xfer-overlay" onClick={() => setTransferTx(null)}>
            <div className="xfer-modal" onClick={e => e.stopPropagation()}>
              <h2>Transfer Sale to Agent</h2>
              <div className="xfer-summary">
                <p><strong>Customer:</strong> {`${transferTx.first_name || ''} ${transferTx.last_name || ''}`.trim() || '—'}</p>
                <p><strong>Amount:</strong> ${Number(transferTx.amount).toFixed(2)}</p>
              </div>
              <div className="xfer-field">
                <label>Credit this sale to*</label>
                <select value={transferAgentId} onChange={e => setTransferAgentId(e.target.value)} disabled={transferAgentsLoading}>
                  <option value="">{transferAgentsLoading ? 'Loading agents…' : 'Select an agent…'}</option>
                  {transferAgents.map(a => (
                    <option key={a.id} value={a.id}>{a.full_name}</option>
                  ))}
                </select>
                {transferAgentsLoading ? (
                  <small>Loading agents…</small>
                ) : transferAgents.length === 0 ? (
                  <small>No eligible agents found in your team.</small>
                ) : null}
              </div>
              <div className="xfer-field">
                <label>Reason (optional)</label>
                <textarea
                  rows={3}
                  value={transferReason}
                  onChange={e => setTransferReason(e.target.value)}
                  placeholder="Why is this sale being credited to this agent?"
                />
              </div>
              <div className="xfer-actions">
                <button className="btn btn-outline-secondary" onClick={() => setTransferTx(null)}>Cancel</button>
                <button
                  className="btn btn-primary"
                  disabled={!transferAgentId || transferSubmitting}
                  onClick={submitTransfer}
                >
                  {transferSubmitting ? 'Transferring…' : 'Transfer'}
                </button>
              </div>
            </div>
            <style>{`
              .xfer-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
              .xfer-modal { background: var(--bg-card, #1f2937); border-radius: 8px; padding: 2rem; width: 90%; max-width: 460px; max-height: 90vh; overflow-y: auto; }
              .xfer-modal h2 { margin: 0 0 1.25rem 0; color: var(--text-primary); }
              .xfer-summary { background: var(--bg-secondary, rgba(255,255,255,0.04)); padding: 1rem; border-radius: 6px; margin-bottom: 1.25rem; }
              .xfer-summary p { margin: 0 0 0.4rem 0; color: var(--text-secondary); }
              .xfer-field { margin-bottom: 1.25rem; }
              .xfer-field label { display: block; margin-bottom: 0.4rem; font-weight: 500; color: var(--text-primary); }
              .xfer-field select, .xfer-field textarea { width: 100%; padding: 0.6rem; border: 1px solid var(--border-primary, #374151); border-radius: 4px; background: var(--bg-primary, #111827); color: var(--text-primary); }
              .xfer-field small { display: block; margin-top: 0.35rem; color: var(--text-secondary); }
              .xfer-actions { display: flex; justify-content: flex-end; gap: 1rem; margin-top: 1.5rem; }
            `}</style>
          </div>
        )}
      </div>

    </DashboardLayout>
  );
};